import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Search, Download, MessageCircle, TrendingUp, RefreshCw,
  Filter, Loader2, Calendar, ChevronDown,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { api, API, getErr, waToCustomer } from "../../lib/api";

const fyForToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() < 3 ? `${y - 1}-${String(y).slice(-2)}` : `${y}-${String(y + 1).slice(-2)}`;
};

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [fys, setFys] = useState([]);
  const [filters, setFilters] = useState({ financial_year: "", month: "", status: "", q: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [inv, sum, fy] = await Promise.all([
        api.get("/admin/invoices", { params }),
        api.get("/admin/invoices/summary", { params: filters.financial_year ? { financial_year: filters.financial_year } : {} }),
        api.get("/admin/invoices/financial-years"),
      ]);
      setInvoices(inv.data.invoices || []);
      setSummary(sum.data);
      setFys(fy.data.financial_years || []);
    } catch (e) { toast.error(getErr(e, "Could not load invoices")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onApply = (e) => { e?.preventDefault(); load(); };
  const onReset = () => { setFilters({ financial_year: "", month: "", status: "", q: "" }); setTimeout(load, 0); };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-invoices-page">
      <Toaster richColors position="top-right" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <span className="label-eyebrow">Compliance</span>
          <h1 className="mt-2 text-3xl font-display font-medium text-[#121C17]">GST Tax Invoices</h1>
          <p className="text-sm text-[#5C6661]">Auto-generated on every payment · sequential numbering per financial year</p>
        </div>
        <button onClick={load} data-testid="invoices-refresh"
          className="self-start inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#E5E2DC] hover:border-[#1E4D3B] hover:text-[#1E4D3B]">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <SummaryStrip summary={summary} />

      <FiltersBar filters={filters} setFilters={setFilters} onApply={onApply} onReset={onReset} fys={fys} />

      {loading ? (
        <div className="py-20 grid place-items-center text-[#5C6661]"><Loader2 className="animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <EmptyState />
      ) : (
        <InvoiceTable invoices={invoices} />
      )}

      <SummaryCharts summary={summary} />
    </div>
  );
}

function SummaryStrip({ summary }) {
  if (!summary) return null;
  const t = summary.totals || {};
  const cards = [
    { label: "Invoices", value: t.invoice_count || 0, color: "#121C17" },
    { label: "Taxable", value: `₹${(t.subtotal || 0).toLocaleString("en-IN")}` },
    { label: "CGST + SGST", value: `₹${((t.cgst || 0) + (t.sgst || 0)).toLocaleString("en-IN")}`, color: "#1E4D3B" },
    { label: "IGST", value: `₹${(t.igst || 0).toLocaleString("en-IN")}`, color: "#D17B5D" },
    { label: "Total tax", value: `₹${(t.total_tax || 0).toLocaleString("en-IN")}`, color: "#1E4D3B" },
    { label: "Grand total", value: `₹${(t.total_amount || 0).toLocaleString("en-IN")}`, highlight: true },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="invoices-summary-strip">
      {cards.map((c) => (
        <div key={c.label}
          data-testid={`summary-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
          className={`p-4 rounded-2xl ${c.highlight ? "bg-[#1E4D3B] text-white" : "bg-white border border-[#E5E2DC]"}`}>
          <div className={`text-[10px] uppercase tracking-wider ${c.highlight ? "text-white/70" : "text-[#5C6661]"}`}>{c.label}</div>
          <div className="mt-1.5 font-display font-medium text-xl" style={{ color: c.highlight ? "white" : (c.color || "#121C17") }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function FiltersBar({ filters, setFilters, onApply, onReset, fys }) {
  return (
    <form onSubmit={onApply} className="bento-card p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="invoices-filters">
      <label className="flex flex-col text-xs">
        <span className="text-[#5C6661] mb-1">Financial year</span>
        <select value={filters.financial_year} onChange={(e) => setFilters({ ...filters, financial_year: e.target.value })}
          data-testid="filter-fy"
          className="bg-white border border-[#E5E2DC] rounded-xl px-3 py-2 text-sm min-w-[120px]">
          <option value="">All</option>
          {fys.map((fy) => <option key={fy} value={fy}>{fy}</option>)}
        </select>
      </label>
      <label className="flex flex-col text-xs">
        <span className="text-[#5C6661] mb-1">Month</span>
        <input type="month" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })}
          data-testid="filter-month"
          className="bg-white border border-[#E5E2DC] rounded-xl px-3 py-2 text-sm" />
      </label>
      <label className="flex flex-col text-xs">
        <span className="text-[#5C6661] mb-1">Status</span>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          data-testid="filter-status"
          className="bg-white border border-[#E5E2DC] rounded-xl px-3 py-2 text-sm min-w-[120px]">
          <option value="">Any</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
          <option value="cancelled">Cancelled</option>
          <option value="draft">Draft</option>
        </select>
      </label>
      <label className="flex-1 min-w-[200px] flex flex-col text-xs">
        <span className="text-[#5C6661] mb-1">Search</span>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-[#5C6661]" />
          <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Invoice #, customer name, mobile, GSTIN"
            data-testid="filter-search"
            className="w-full pl-9 bg-white border border-[#E5E2DC] rounded-xl px-3 py-2 text-sm" />
        </div>
      </label>
      <div className="flex gap-2">
        <button type="submit" data-testid="filter-apply"
          className="btn-primary rounded-full px-5 py-2 text-sm font-medium inline-flex items-center gap-2">
          <Filter size={14} /> Apply
        </button>
        <button type="button" onClick={onReset} data-testid="filter-reset"
          className="text-sm px-4 py-2 rounded-full border border-[#E5E2DC] hover:border-[#1E4D3B]">
          Reset
        </button>
      </div>
    </form>
  );
}

function InvoiceTable({ invoices }) {
  return (
    <div className="bento-card overflow-hidden" data-testid="invoices-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F1EC] text-[#5C6661]">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Invoice #</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium text-right">Subtotal</th>
              <th className="px-4 py-3 font-medium text-right">Tax</th>
              <th className="px-4 py-3 font-medium text-right">Total</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceRow({ inv }) {
  const onWaShare = async () => {
    const wa = waToCustomer(inv.customer_mobile, buildMsg(inv));
    window.open(wa, "_blank", "noopener,noreferrer");
    try {
      await api.post(`/admin/invoices/${inv.id}/whatsapp-log`, {
        channel: "wa.me", template: "tax_invoice_share",
        message: buildMsg(inv).slice(0, 280),
      });
    } catch { /* best-effort */ }
  };
  const statusColor = {
    paid: "bg-emerald-100 text-emerald-700",
    refunded: "bg-amber-100 text-amber-700",
    cancelled: "bg-red-100 text-red-700",
    draft: "bg-gray-100 text-gray-700",
  }[inv.status] || "bg-gray-100 text-gray-700";

  return (
    <tr className="border-t border-[#E5E2DC] hover:bg-[#F3F1EC]/50" data-testid={`invoice-row-${inv.invoice_seq}`}>
      <td className="px-4 py-3 font-mono text-[12px]">
        <Link to={`/admin/invoices/${inv.id}`} className="text-[#1E4D3B] hover:underline">
          {inv.invoice_number}
        </Link>
      </td>
      <td className="px-4 py-3 text-[#5C6661]">{(inv.invoice_date || "").slice(0, 10)}</td>
      <td className="px-4 py-3">
        <div className="font-medium text-[#121C17]">{inv.customer_name || "—"}</div>
        <div className="text-[11px] text-[#5C6661]">+91 {inv.customer_mobile}</div>
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${inv.gst_type === "IGST" ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"}`}>
          {inv.gst_type === "IGST" ? "IGST" : "CGST+SGST"}
        </span>
      </td>
      <td className="px-4 py-3 text-right">₹{(inv.subtotal || 0).toLocaleString("en-IN")}</td>
      <td className="px-4 py-3 text-right">₹{(inv.total_tax || 0).toLocaleString("en-IN")}</td>
      <td className="px-4 py-3 text-right font-medium">₹{(inv.total_amount || 0).toLocaleString("en-IN")}</td>
      <td className="px-4 py-3">
        <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${statusColor}`}>{inv.status}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 justify-end">
          <a href={`${API}/admin/invoices/${inv.id}/pdf`} target="_blank" rel="noopener noreferrer"
            data-testid={`invoice-download-${inv.invoice_seq}`}
            className="text-[#1E4D3B] hover:bg-[#1E4D3B]/10 p-2 rounded-lg" title="Download PDF">
            <Download size={14} />
          </a>
          <button onClick={onWaShare} data-testid={`invoice-wa-${inv.invoice_seq}`}
            className="p-2 rounded-lg" style={{ color: "#25D366" }} title="Share on WhatsApp">
            <MessageCircle size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function buildMsg(inv) {
  return [
    `Hello ${inv.customer_name || ""},`, "",
    "Your payment has been successfully received.", "",
    "✅ GST Tax Invoice Generated",
    `📄 Invoice No: ${inv.invoice_number}`,
    `💰 Amount: ₹${(inv.total_amount || 0).toLocaleString("en-IN")}`,
    "🧾 GST Included", "",
    "Your invoice PDF is attached below.", "",
    "– Team Finsahayak.com",
  ].join("\n");
}

function EmptyState() {
  return (
    <div className="bento-card p-12 text-center" data-testid="invoices-empty">
      <FileText size={32} className="mx-auto text-[#5C6661] mb-3" />
      <h3 className="font-display font-medium text-lg text-[#121C17] mb-1">No invoices yet</h3>
      <p className="text-sm text-[#5C6661] max-w-md mx-auto">
        Invoices are auto-generated when payments are recorded (manual or Razorpay). Try recording a payment from any customer detail page.
      </p>
    </div>
  );
}

function SummaryCharts({ summary }) {
  if (!summary || !summary.monthly || summary.monthly.length === 0) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-5 mt-5">
      <div className="bento-card p-5" data-testid="chart-monthly-tax">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-[#1E4D3B]" />
          <h2 className="font-display font-medium text-lg text-[#121C17]">Monthly GST collected</h2>
        </div>
        <div className="h-64 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={summary.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
              <XAxis dataKey="month" stroke="#5C6661" tick={{ fontSize: 11 }} />
              <YAxis stroke="#5C6661" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="cgst" stackId="a" fill="#1E4D3B" name="CGST" />
              <Bar dataKey="sgst" stackId="a" fill="#7BBF99" name="SGST" />
              <Bar dataKey="igst" stackId="a" fill="#D17B5D" name="IGST" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bento-card p-5" data-testid="chart-monthly-revenue">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={16} className="text-[#1E4D3B]" />
          <h2 className="font-display font-medium text-lg text-[#121C17]">Monthly revenue</h2>
        </div>
        <div className="h-64 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={summary.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
              <XAxis dataKey="month" stroke="#5C6661" tick={{ fontSize: 11 }} />
              <YAxis stroke="#5C6661" tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total_amount" stroke="#1E4D3B" name="Total" strokeWidth={2.5} />
              <Line type="monotone" dataKey="subtotal" stroke="#D17B5D" name="Taxable" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
